/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Filter state, mirrored into the URL query string.
 *
 * The URL is the source of truth, not a copy of it. Support engineers paste these links into
 * tickets constantly, so a filtered view has to survive a reload and mean the same thing to the
 * next person who opens it.
 */

import { useCallback, useMemo } from 'react'

import { useSearchParams } from 'react-router-dom'

import type { AuditFilters, ChangeType, EntityType, TimePreset } from './types'
import { CHANGE_TYPES, ENTITY_TYPES } from './types'

const TIME_PRESETS: TimePreset[] = ['24h', '7d', '30d', '90d', 'custom']

/**
 * Written when the user deselects every company.
 *
 * An absent `companyId` means "not chosen yet", which adopts the app's company; an empty selection
 * is a deliberate choice and has to survive a reload and a shared link, so it needs a value of its
 * own rather than the absence of one.
 */
const NO_COMPANY = 'none'

/** Keep only values this build understands, so a hand-edited URL cannot inject nonsense. */
const readEnum = <T extends string>(params: URLSearchParams, key: string, allowed: readonly T[]): T[] =>
  params.getAll(key).filter((value): value is T => (allowed as readonly string[]).includes(value))

export const defaultFilters = (companyIds: number[]): AuditFilters => ({
  companyIds,
  time: '7d',
  changeTypes: [],
  entityTypes: [],
  userIds: [],
  search: '',
})

/** True when the filter differs from the default view, which is what "Clear all" acts on. */
export const activeFilterCount = (filters: AuditFilters): number => {
  let n = 0
  if (filters.time !== '7d') n++
  if (filters.changeTypes.length) n++
  if (filters.entityTypes.length) n++
  if (filters.userIds.length) n++
  if (filters.entityId) n++
  if (filters.search.trim()) n++
  return n
}

export interface UseAuditFiltersResult {
  filters: AuditFilters
  setFilters: (patch: Partial<AuditFilters>) => void
  clearFilters: () => void
  /** The query string (with leading `?`) the URL would carry after applying `patch`. */
  toSearch: (patch: Partial<AuditFilters>) => string
  activeCount: number
}

/**
 * @param fallbackCompanyIds the app's selected company, used until the URL carries its own.
 */
export const useAuditFilters = (fallbackCompanyIds: number[]): UseAuditFiltersResult => {
  const [params, setParams] = useSearchParams()

  // Both serialised, so the memo is stable across renders that changed neither. The filters object
  // is the react-query key, so an unstable identity here would refetch on every render.
  const search = params.toString()
  const fallbackKey = fallbackCompanyIds.join(',')

  const filters = useMemo<AuditFilters>(() => {
    const current = new URLSearchParams(search)
    const fallback = fallbackKey ? fallbackKey.split(',').map(Number) : []
    const companyParams = current.getAll('companyId')
    const companyIds = companyParams.map(Number).filter((id) => Number.isInteger(id))
    const time = current.get('time')

    return {
      companyIds: companyParams.length > 0 ? companyIds : fallback,
      time: TIME_PRESETS.includes(time as TimePreset) ? (time as TimePreset) : '7d',
      from: current.get('from') ?? undefined,
      to: current.get('to') ?? undefined,
      changeTypes: readEnum<ChangeType>(current, 'changeType', CHANGE_TYPES),
      entityTypes: readEnum<EntityType>(current, 'entityType', ENTITY_TYPES),
      userIds: current.getAll('userId').filter(Boolean),
      entityId: current.get('entityId') ?? undefined,
      search: current.get('q') ?? '',
    }
  }, [search, fallbackKey])

  const serialize = useCallback((next: AuditFilters) => {
    const params = new URLSearchParams()

    if (next.companyIds.length === 0) params.set('companyId', NO_COMPANY)
    else next.companyIds.forEach((id) => params.append('companyId', String(id)))
    if (next.time !== '7d') params.set('time', next.time)
    if (next.time === 'custom') {
      if (next.from) params.set('from', next.from)
      if (next.to) params.set('to', next.to)
    }
    next.changeTypes.forEach((v) => params.append('changeType', v))
    next.entityTypes.forEach((v) => params.append('entityType', v))
    next.userIds.forEach((v) => params.append('userId', v))
    if (next.entityId) params.set('entityId', next.entityId)
    if (next.search.trim()) params.set('q', next.search.trim())
    return params
  }, [])

  const write = useCallback(
    (next: AuditFilters) => {
      const params = serialize(next)

      // Replace rather than push: a filter tweak is not a navigation step, and pushing would make
      // Back walk through every keystroke of the search box.
      setParams(params, { replace: true })
    },
    [serialize, setParams]
  )

  const setFilters = useCallback(
    (patch: Partial<AuditFilters>) => {
      write({ ...filters, ...patch })
    },
    [filters, write]
  )

  const toSearch = useCallback(
    (patch: Partial<AuditFilters>) => {
      const query = serialize({ ...filters, ...patch }).toString()
      return query ? `?${query}` : ''
    },
    [filters, serialize]
  )

  const clearFilters = useCallback(() => {
    write(defaultFilters(filters.companyIds))
  }, [filters.companyIds, write])

  return { filters, setFilters, clearFilters, toSearch, activeCount: activeFilterCount(filters) }
}
